// src/FreelancerAlertsPanel.jsx
import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Card, CardContent, Typography, Box, Stack, CircularProgress, Alert,
  List, ListItemButton, ListItemText, Chip, Button, Collapse, ListItemIcon
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// Endpoints candidati (prova prima quello locale del frontend, poi /api, poi backend diretto)
import { API_BASE } from './api'; // assicurati che sia presente in alto
import { useNavigate } from 'react-router-dom';
const CANDIDATE_BASES = [API_BASE];


/* =========================================================
   Normalizzazione macro + ruolo → macro
   ========================================================= */
function normMacro(s) {
  if (!s) return null;
  const k = String(s).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
  if (k.includes('POST') && k.includes('PRODUZIONE')) return 'POSTPRODUZIONE';
  if (k.includes('PRODUZIONE') && !k.includes('POST')) return 'PRODUZIONE';
  if (k.includes('REDAZIONE')) return 'REDAZIONE';
  if (k.includes('SET')) return 'SET';
  return k || null;
}

// mappa essenziale: puoi arricchirla in base al tuo roles-map.js
const ROLE_KEYWORDS = {
  POSTPRODUZIONE: [
    'montatore', 'editor', 'colorist', 'finalizzatore', 'post', 'motion', 'grafico', 'assistente al montaggio'
  ],
  PRODUZIONE: [
    'producer', 'organizzazione', 'produzione', 'segretaria di produzione', 'location manager'
  ],
  REDAZIONE: [
    'autore', 'redattore', 'caporedattore', 'runner redazione'
  ],
  SET: [
    'operatore', 'dop', 'camera', 'fonico', 'presa diretta', 'elettricista', 'macchinista'
  ],
};
function rolesToMacros(roles = []) {
  const out = new Set();
  const norm = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  for (const r of roles) {
    const rr = norm(r);
    for (const [macro, list] of Object.entries(ROLE_KEYWORDS)) {
      if (list.some(k => rr.includes(k))) out.add(macro);
    }
  }
  return Array.from(out);
}

/* =========================================================
   Fetch robusto
   ========================================================= */
async function tryFetchJson(url) {
  const resp = await fetch(url, { method: 'GET', cache: 'no-store' });
  const raw = await resp.text();
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  try { return JSON.parse(raw); }
  catch { throw new Error(`Not JSON from ${url}: ${raw?.slice(0, 100)}`); }
}

/* =========================================================
   Helpers ruolo + date (già presenti)
   ========================================================= */
function pickRoleDirect(it) {
  return (
    it.ruolo || it.role || it.ruoloLabel || it.role_name ||
    it.position || it.posizione || it.job_title || it.job || null
  );
}
function extractRoleFromText(t, fallbackTitle) {
  if (!t && !fallbackTitle) return null;
  const s = `${fallbackTitle || ''}\n${t || ''}`;

  let m = s.match(/(?:ruolo\s*(?:ricercato)?|posizione)\s*:\s*([^\n]+)/i);
  if (m) return m[1].trim();

  m = s.match(/Richiesta\s+([^\n]+)/i);
  if (m) return m[1].trim();

  m = s.match(/(?:cerchiamo|si\s+ricerca|ricerchiamo)\s+un[oa]?\s+([^\n]+)/i);
  if (m) return m[1].trim();

  if (fallbackTitle) {
    const norm = fallbackTitle.trim().toUpperCase();
    if (!/^ALERT\s+(POSTPRODUZIONE|PRODUZIONE|REDAZIONE|SET)\b/.test(norm)) {
      return fallbackTitle.trim();
    }
  }
  return null;
}
function parseItalianishDate(str) {
  if (!str) return null;
  let d = null;

  let m = str.match(/^(\d{4})[\/-](\d{1,2})[\/-](\d{1,2})$/);
  if (m) {
    const [, y, mo, da] = m.map(Number);
    d = new Date(y, mo - 1, da);
    return isNaN(d.getTime()) ? null : d;
  }

  m = str.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})$/);
  if (m) {
    let [, da, mo, y] = m;
    da = Number(da); mo = Number(mo); y = Number(y.length === 2 ? '20' + y : y);
    d = new Date(y, mo - 1, da);
    return isNaN(d.getTime()) ? null : d;
  }

  return null;
}
function extractDatesFromText(t) {
  if (!t) return null;
  const all = [...t.matchAll(/\b(\d{1,2}[\/-]\d{1,2}[\/-]\d{2,4}|\d{4}[\/-]\d{1,2}[\/-]\d{1,2})\b/g)].map(m => m[1]);
  if (!all.length) return null;

  const parsed = all.map(parseItalianishDate).filter(Boolean).sort((a, b) => a - b);
  if (!parsed.length) return null;

  const f = (d) => d?.toLocaleDateString?.('it-IT') ?? null;
  if (parsed.length >= 2) return `${f(parsed[0])} → ${f(parsed[parsed.length - 1])}`;
  return `dal ${f(parsed[0])}`;
}
function pickDates(it) {
  if (it.date_from || it.date_to) {
    const f = (d) => d ? new Date(d).toLocaleDateString('it-IT') : null;
    if (it.date_from && it.date_to) return `${f(it.date_from)} → ${f(it.date_to)}`;
    if (it.date_from) return `dal ${f(it.date_from)}`;
    if (it.date_to) return `fino al ${f(it.date_to)}`;
  }
  const direct =
    it.from || it.start_date || it.startDate || it.dal || it.dataDal ||
    it.to || it.end_date || it.endDate || it.al || it.dataAl;
  if (direct) {
    const from =
      parseItalianishDate(it.from) || parseItalianishDate(it.start_date) ||
      parseItalianishDate(it.startDate) || parseItalianishDate(it.dal) || parseItalianishDate(it.dataDal);
    const to =
      parseItalianishDate(it.to) || parseItalianishDate(it.end_date) ||
      parseItalianishDate(it.endDate) || parseItalianishDate(it.al) || parseItalianishDate(it.dataAl);
    const f = (d) => d?.toLocaleDateString?.('it-IT') ?? null;
    if (from && to) return `${f(from)} → ${f(to)}`;
    if (from) return `dal ${f(from)}`;
    if (to) return `fino al ${f(to)}`;
  }
  return extractDatesFromText([it.description, it.text, it.message, it.body].filter(Boolean).join('\n'));
}
function pickRole(it) {
  // Prima prova campi diretti
  const direct = pickRoleDirect(it);
  if (direct && direct.toUpperCase() !== 'POSTPRODUZIONE' && direct.toUpperCase() !== 'PRODUZIONE') return direct;

  // Prova extractRoleFromText
  const extracted = extractRoleFromText(
    [it.description, it.text, it.message, it.body].filter(Boolean).join('\n'),
    it.title
  );
  if (extracted) return extracted;

  // Prendi prima riga significativa della description (non vuota, non solo maiuscole tipo "NUOVO ALERT")
  const desc = it.description || it.text || it.message || it.body || '';
  const lines = desc.split('\n').map(l => l.trim()).filter(l => l.length > 3 && !/^[A-Z\s📢🔔!]+$/.test(l));
  if (lines.length > 0) return lines[0].length > 60 ? lines[0].slice(0, 60) + '…' : lines[0];

  // Fallback al titolo se non è solo categoria
  if (it.title && !/^ALERT\s+(POSTPRODUZIONE|PRODUZIONE|REDAZIONE|SET)/i.test(it.title)) return it.title;

  return it.macro_group || 'Alert di lavoro';
}

/* =========================================================
   COMPONENT
   - NUOVE PROP: roles (array di ruoli) e/o macros (array di macro già pronti)
   - Se non arrivano, tenta da window.__FREELANCER_ROLES__ o senza filtro (back-compat)
   ========================================================= */
export default function FreelancerAlertsPanel({
  macroGroup = null,
  days = 14,
  maxItems = 15,
  roles = [],           // ⬅️ nuovo (opzionale)
  macros = [],          // ⬅️ nuovo (opzionale, ha priorità su roles)
  hideTitle = false,    // opzionale, per gestire il titolo esterno/interno
  token = null,         // token JWT per la messaggistica interna
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debugUrl, setDebugUrl] = useState('');
  const [baseFound, setBaseFound] = useState('');

  const [openId, setOpenId] = useState(null);
  const [contactSending, setContactSending] = useState(false);
  const [contactError, setContactError] = useState(null);

  // flag per nascondere il pannello su viste non freelance
  const hide = typeof window !== 'undefined' && window.__HIDE_ALERTS__ === true;

  // calcola i macro desiderati
  const desiredMacros = useMemo(() => {
    // 1) se arrivano esplicitamente
    if (Array.isArray(macros) && macros.length) return macros.map(normMacro).filter(Boolean);
    // 2) se arrivano ruoli
    if (Array.isArray(roles) && roles.length) return rolesToMacros(roles).map(normMacro).filter(Boolean);
    // 3) prova da global
    const g = typeof window !== 'undefined' && Array.isArray(window.__FREELANCER_ROLES__)
      ? rolesToMacros(window.__FREELANCER_ROLES__)
      : [];
    return g.map(normMacro).filter(Boolean);
  }, [macros, roles]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      let lastErr = null;

      // costruisci query string con macros se presenti
      const qs = new URLSearchParams({
        days: String(days),
        limit: String(maxItems),
      });
      if (desiredMacros.length) {
        qs.set('macros', desiredMacros.join(','));
      }

      for (const base of CANDIDATE_BASES) {
        const url = `${base}/alerts?${qs.toString()}`;
        setDebugUrl(url);
        try {
          const data = await tryFetchJson(url);
          if (cancelled) return;
          setBaseFound(base);
          setItems(Array.isArray(data) ? data : []);
          setLoading(false);
          return;
        } catch (e) {
          lastErr = e;
        }
      }
      if (!cancelled) {
        setError(lastErr?.message || 'Nessun endpoint /alerts trovato');
        setItems([]);
        setLoading(false);
      }
    }
    if (!hide) load();
    return () => { cancelled = true; };
  }, [days, maxItems, hide, desiredMacros.join(',')]); // 🔴 ricarica se cambiano i macro

  // filtro locale opzionale per macroGroup (singolo) se passato
  const filtered = useMemo(() => {
    if (!macroGroup) return items;
    const mg = normMacro(macroGroup);
    return items.filter(it => normMacro(it.category || it.macroGroup) === mg);
  }, [items, macroGroup]);

  const onOpenDetail = (id) => setOpenId(openId === id ? null : id);

  // Crea/trova conversazione col coordinatore e vai alla messaggistica interna
  const handleContact = async (alert) => {
console.log('[handleContact] alert.created_by:', alert.created_by, 'token:', token);
    const coordinatorId = alert.created_by;
    if (!coordinatorId) {
      setContactError('Questo alert non ha un coordinatore collegato.');
      return;
    }
    if (!token) {
      setContactError('Devi essere autenticato per inviare un messaggio.');
      return;
    }
    setContactSending(true);
    setContactError(null);
    try {
      const base = baseFound || CANDIDATE_BASES[0];
      const resp = await fetch(`${base}/messages/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ otherUserId: coordinatorId })
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`);
      // Redirect alla pagina messaggi con la conversazione aperta
      navigate(`/messages?cid=${data.id}`);
    } catch (e) {
      setContactError(e.message || String(e));
    } finally {
      setContactSending(false);
    }
  };

  if (hide) return null;

  if (loading) {
    return (
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={20} /><Typography>Caricamento alert…</Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Alert severity="error">
        Errore nel caricamento degli alert: {error}
        <br/><code>{debugUrl}</code>
      </Alert>
    );
  }
  if (!filtered.length) {
    return <Alert severity="info">{t('alerts.no_recent')}</Alert>;
  }

  return (
    <Box>
      <Box>
          {/* Titolo opzionale */}
          {!hideTitle && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}>
                {t('alerts.recent_title')}
              </Typography>
              {desiredMacros.length > 0 && (
                <Box sx={{ px: 1, py: 0.25, borderRadius: '4px', background: 'rgba(212,165,80,0.1)', border: '1px solid rgba(212,165,80,0.3)' }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#d4a550', letterSpacing: '0.06em' }}>{desiredMacros.join(' · ')}</Typography>
                </Box>
              )}
            </Box>
          )}

          <List dense sx={{ maxHeight: 520, overflowY: 'auto', pr: 0.5 }}>
            {filtered.map((it) => {
              const id = it.id || `${it.title}-${it.created_at || ''}`;
              const role = pickRole(it);
              const range = pickDates(it);

              return (
                <Box key={id}>
                  <ListItemButton
                    onClick={() => onOpenDetail(id)}
                    sx={{ borderRadius: '8px', mb: 0.5, '&:hover': { backgroundColor: 'rgba(212,165,80,0.04)' } }}
                  >
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <ChevronRightIcon
                        sx={{ transform: openId === id ? 'rotate(90deg)' : 'none', transition: '0.15s' }}
                      />
                    </ListItemIcon>

                    <ListItemText
                      primary={
                        <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#e8e6df' }} component="div">
                          {role}
                          {range && (
                            <Typography component="span" sx={{ fontWeight: 400, ml: 1, fontSize: 12, color: 'text.secondary' }}>
                              — {range}
                            </Typography>
                          )}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display:'flex', gap: 1, alignItems:'center', mt: 0.4, flexWrap: 'wrap' }}>
                          <Typography sx={{ fontSize: 10, color: "text.disabled", letterSpacing: "0.04em" }}>Espandi →</Typography>
                        </Box>
                      }
                      primaryTypographyProps={{ component: 'div' }}
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItemButton>

                  <Collapse in={openId === id} timeout="auto" unmountOnExit>
                    <Box sx={{ pl: 5, pr: 1, pb: 1.5 }}>
                      {!!it.description && (
                        <Typography sx={{ whiteSpace: 'pre-wrap', mb: 1.25, fontSize: 12, color: 'text.secondary', lineHeight: 1.6 }}>
                          {it.description}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap:'wrap' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={contactSending || !it.created_by}
                          onClick={() => handleContact(it)}
                          sx={{ borderColor: 'rgba(212,165,80,0.4)', color: '#d4a550', fontSize: 11, fontWeight: 600, borderRadius: '6px', textTransform: 'none', '&:hover': { background: 'rgba(212,165,80,0.08)', borderColor: '#d4a550' } }}
                        >
                          {contactSending ? 'Apertura...' : 'Contatta'}
                        </Button>
                        {contactError && (
                          <Alert severity="error" sx={{ mt: 1 }}>{contactError}</Alert>
                        )}
                        {!!it.contact && <Typography variant="body2" sx={{ alignSelf:'center' }}>Contatti: {it.contact}</Typography>}
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </List>
      </Box>
    </Box>
  );
}
